/**
 * Navigation Accessibility Hook
 *
 * Single Responsibility: Handles only navigation and accessibility data fetching
 * Dependency Inversion: Uses service abstraction (fetchQuickPeekNavigationAccessibility)
 * Open/Closed: Extensible through options, closed for modification
 */

import { useMutation } from '@tanstack/react-query';
import { fetchQuickPeekNavigationAccessibility } from '@/services/travelAssistantService';
import {
  QuickPeekRequest,
  QuickPeekResponse,
} from '@/services/api/interfaces/ITravelAssistantRepository';

export interface UseNavigationAccessibilityOptions {
  accessToken?: string;
  refreshToken?: string;
}

export interface UseNavigationAccessibilityResult {
  data: Partial<QuickPeekResponse> | null;
  isLoading: boolean;
  error: Error | null;
  fetch: (
    request: QuickPeekRequest,
    signal?: AbortSignal
  ) => Promise<Partial<QuickPeekResponse> | null>;
  reset: () => void;
}

/**
 * Hook for fetching navigation and accessibility data
 * Follows SOLID principles:
 * - Single Responsibility: Only handles navigation/accessibility data
 * - Dependency Inversion: Depends on service abstraction
 * - Open/Closed: Extensible via options
 *
 * @param options - Optional configuration including access and refresh tokens
 * @returns Object containing navigation/accessibility data, loading state, error, and fetch function
 */
export function useNavigationAccessibility(
  options?: UseNavigationAccessibilityOptions
): UseNavigationAccessibilityResult {
  const mutation = useMutation({
    mutationFn: async ({
      request,
      signal,
    }: {
      request: QuickPeekRequest;
      signal?: AbortSignal;
    }) => {
      return fetchQuickPeekNavigationAccessibility(
        request,
        options?.accessToken,
        options?.refreshToken,
        signal
      );
    },
    onSuccess: (data, variables) => {
      console.log(
        '✅ [useNavigationAccessibility] Data fetched successfully:',
        {
          hasData: !!data,
          location: variables.request.location,
          dataKeys: data ? Object.keys(data) : [],
        }
      );
    },
    retry: (failureCount, error) => {
      // Don't retry if the request was canceled
      if (
        (error as Error & { code?: string })?.code === 'ERR_CANCELED' ||
        (error as Error & { name?: string })?.name === 'CanceledError' ||
        (error as Error).message === 'canceled'
      ) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  const fetch = async (
    request: QuickPeekRequest,
    signal?: AbortSignal
  ): Promise<Partial<QuickPeekResponse> | null> => {
    try {
      const response = await mutation.mutateAsync({ request, signal });
      return response;
    } catch (err) {
      // Ignore cancellation errors
      if (
        (err as Error & { code?: string })?.code === 'ERR_CANCELED' ||
        (err as Error & { name?: string })?.name === 'CanceledError' ||
        (err as Error).message === 'canceled'
      ) {
        console.log('🛑 [useNavigationAccessibility] Request cancelled');
        return null;
      }

      const error =
        err instanceof Error
          ? err
          : new Error('Failed to fetch navigation/accessibility data');
      console.error('Error fetching navigation/accessibility:', error);
      return null;
    }
  };

  const reset = () => {
    mutation.reset();
  };

  return {
    data: mutation.data || null,
    isLoading: mutation.isPending,
    error: mutation.error as Error | null,
    fetch,
    reset,
  };
}
